'use client';

import * as React from 'react';
import {
  ChatError,
  chatErrorText,
  fetchMeetingChat,
  sendMeetingChat,
  type ChatMessage,
  type ChatRequest,
  type ChatStatus,
  type MeetingChatState,
} from '@/lib/assistant-chat';
import { ru } from '@/lib/ru';

export const CHECKLIST_CHIP = 'meeting_checklist';

interface Pending {
  request: ChatRequest;
  startedAt: number;
  statuses: ChatStatus[];
}

export interface BotChat {
  state: MeetingChatState | null;
  pending: Pending | null;
  failed: { request: ChatRequest; text: string } | null;
  nowMs: number;
  draft: string;
  setDraft: (value: string) => void;
  send: (request: ChatRequest, shown: string) => Promise<void>;
  sendDraft: () => void;
  pickChip: (chipId: string, label: string) => void;
  abort: () => void;
  reload: () => void;
}

/**
 * The AGIMA Bot chat of this meeting: the same thread as on the meeting page
 * of the admin UI. The bot writes the checklist straight into the meeting;
 * the assistant picks the new agenda up live.
 */
export function useBotChat(
  meetingId: string | null | undefined,
  onReplied?: (state: MeetingChatState) => void,
): BotChat {
  const [state, setState] = React.useState<MeetingChatState | null>(null);
  const [draft, setDraft] = React.useState('');
  const [pending, setPending] = React.useState<Pending | null>(null);
  const [failed, setFailed] = React.useState<BotChat['failed']>(null);
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  const abortRef = React.useRef<AbortController | null>(null);
  const pendingRef = React.useRef(false);

  const load = React.useCallback(
    (notify = false) => {
      if (!meetingId) return;
      fetchMeetingChat(meetingId)
        .then((next) => {
          // A poll that brought nothing new keeps the same object: no re-render, no scroll.
          setState((prev) => (prev && sameChat(prev, next) ? prev : next));
          if (notify) onReplied?.(next);
        })
        .catch(() => {
          if (!notify) setState((prev) => prev ?? { enabled: false, available: false, chips: [], messages: [] });
        });
    },
    [meetingId, onReplied],
  );

  React.useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  // The feed is shared by everyone in the call: pick up the others' questions
  // and the bot's answers to them. Not while our own answer streams in.
  React.useEffect(() => {
    if (!meetingId) return;
    const timer = window.setInterval(() => {
      if (!pendingRef.current && !document.hidden) load();
    }, CHAT_POLL_MS);
    return () => window.clearInterval(timer);
  }, [meetingId, load]);

  React.useEffect(() => {
    if (!pending) return;
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [pending]);

  const send = async (request: ChatRequest, shown: string) => {
    if (!meetingId || pendingRef.current) return;
    pendingRef.current = true;
    const controller = new AbortController();
    abortRef.current = controller;
    setFailed(null);
    const startedAt = Date.now();
    setNowMs(startedAt);
    setPending({ request, startedAt, statuses: [] });
    setState(
      (s) =>
        s && {
          ...s,
          messages: [
            ...s.messages,
            {
              id: `local-${startedAt}`,
              role: 'user',
              mine: true,
              text: shown,
              createdAt: new Date(startedAt).toISOString(),
            },
          ],
        },
    );
    try {
      const reply = await sendMeetingChat(meetingId, request, {
        signal: controller.signal,
        onStatus: (status) =>
          setPending((p) => p && { ...p, statuses: [...p.statuses, status].slice(-3) }),
      });
      const answer: ChatMessage = {
        id: `local-${Date.now()}`,
        role: 'assistant',
        mine: true,
        text: reply,
        durationMs: Date.now() - startedAt,
        createdAt: new Date().toISOString(),
      };
      setState((s) => s && { ...s, messages: [...s.messages, answer] });
      load(true);
    } catch (error) {
      if ((error as Error).name === 'AbortError')
        setFailed({ request, text: chatErrorText('aborted') });
      else
        setFailed({
          request,
          text: error instanceof ChatError ? error.message : chatErrorText('unavailable'),
        });
    } finally {
      abortRef.current = null;
      pendingRef.current = false;
      setPending(null);
    }
  };

  const sendDraft = () => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    void send({ text }, text);
  };

  // A typed draft goes along with the chip: materials for the checklist, a
  // remark for the rest.
  const pickChip = (chipId: string, label: string) => {
    const typed = draft.trim();
    setDraft('');
    if (chipId === CHECKLIST_CHIP) {
      void send(
        typed ? { chip: chipId, chipInput: typed } : { chip: chipId },
        typed ? `${label}\n${ru.botMaterials}: ${typed.length}` : label,
      );
    } else {
      void send(
        typed ? { chip: chipId, text: typed } : { chip: chipId },
        typed ? `${label}\n${typed}` : label,
      );
    }
  };

  return {
    state,
    pending,
    failed,
    nowMs,
    draft,
    setDraft,
    send,
    sendDraft,
    pickChip,
    abort: () => abortRef.current?.abort(),
    reload: () => load(),
  };
}

const CHAT_POLL_MS = 5000;
// An unanswered question older than this is not «being answered» any more.
const OTHERS_PENDING_MS = 10 * 60 * 1000;

function sameChat(a: MeetingChatState, b: MeetingChatState): boolean {
  const last = (s: MeetingChatState) => s.messages[s.messages.length - 1]?.id ?? '';
  return (
    a.enabled === b.enabled &&
    a.available === b.available &&
    a.assistantEnabled === b.assistantEnabled &&
    a.messages.length === b.messages.length &&
    last(a) === last(b) &&
    a.chips.map((c) => c.id).join() === b.chips.map((c) => c.id).join()
  );
}

/** Who is waiting for the bot right now, apart from me: their last message is a question. */
function othersWaiting(messages: ChatMessage[], nowMs: number): string[] {
  const lastByLogin = new Map<string, ChatMessage>();
  for (const message of messages) {
    if (message.mine || !message.login) continue;
    lastByLogin.set(message.login, message);
  }
  return [...lastByLogin.values()]
    .filter((m) => m.role === 'user' && nowMs - new Date(m.createdAt).getTime() < OTHERS_PENDING_MS)
    .map((m) => m.name || m.login || '');
}

// [text](https://…), bare https://… and **bold**; links only to http(s).
const INLINE = /(\[[^\]]+\]\(https?:\/\/[^\s)]+\))|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?»"'])|(\*\*[^*]+\*\*)/g;

function inline(value: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const match of value.matchAll(INLINE)) {
    const at = match.index ?? 0;
    if (at > last) out.push(value.slice(last, at));
    const [whole, md, bare, bold] = match;
    if (md) {
      const label = md.slice(1, md.indexOf(']('));
      const href = md.slice(md.indexOf('](') + 2, -1);
      out.push(
        <a key={key++} href={href} target="_blank" rel="noopener noreferrer">
          {label}
        </a>,
      );
    } else if (bare) {
      out.push(
        <a key={key++} href={bare} target="_blank" rel="noopener noreferrer">
          {bare.replace(/^https?:\/\//, '').replace(/\?utm_source=[^&]+$/, '')}
        </a>,
      );
    } else if (bold) {
      out.push(<strong key={key++}>{inline(bold.slice(2, -2))}</strong>);
    }
    last = at + whole.length;
  }
  if (last < value.length) out.push(value.slice(last));
  return out;
}

/** Minimal markdown for the bot's answers: headings, lists, tables, bold, links. */
function Markdown({ text }: { text: string }) {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  const isTable = (line: string) => /^\s*\|.*\|\s*$/.test(line);
  const isDivider = (line: string) =>
    /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line);
  const cells = (line: string) =>
    line
      .trim()
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map((cell) => cell.trim());
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i] ?? '';
    if (!line.trim()) {
      i += 1;
      continue;
    }
    const heading = line.match(/^#{1,4}\s+(.*)$/);
    if (heading) {
      blocks.push(
        <p key={key++} className="agima-bot-heading">
          {inline(heading[1] ?? '')}
        </p>,
      );
      i += 1;
      continue;
    }
    if (isTable(line) && isDivider(lines[i + 1] ?? '')) {
      const head = cells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && isTable(lines[i] ?? '')) rows.push(cells(lines[i++] ?? ''));
      blocks.push(
        <div key={key++} className="agima-bot-table-wrap">
          <table className="agima-bot-table">
            <thead>
              <tr>
                {head.map((cell, c) => (
                  <th key={c}>{inline(cell)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c}>{inline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    const listItem = /^\s*(?:[-*]|\d+[.)])\s+/;
    if (listItem.test(line)) {
      const items: string[] = [];
      while (i < lines.length && listItem.test(lines[i] ?? ''))
        items.push((lines[i++] ?? '').replace(listItem, ''));
      blocks.push(
        <ul key={key++} className="agima-bot-list">
          {items.map((item, n) => (
            <li key={n}>{inline(item)}</li>
          ))}
        </ul>,
      );
      continue;
    }
    const paragraph: string[] = [];
    while (
      i < lines.length &&
      (lines[i] ?? '').trim() &&
      !/^#{1,4}\s/.test(lines[i] ?? '') &&
      !listItem.test(lines[i] ?? '') &&
      !(isTable(lines[i] ?? '') && isDivider(lines[i + 1] ?? ''))
    ) {
      paragraph.push(lines[i++] ?? '');
    }
    blocks.push(<p key={key++}>{inline(paragraph.join('\n'))}</p>);
  }
  return <div className="agima-bot-markdown">{blocks}</div>;
}

/** The same green orb as the AGIMA Bot widget in Jira. */
export function AgimaBotIcon(props: { size?: number }) {
  const size = props.size ?? 20;
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      className="agima-bot-orb"
      src="/images/agima-bot-orb.png"
      alt=""
      aria-hidden="true"
      width={size}
      height={size}
      style={{ width: size, height: size }}
    />
  );
}

/** The conversation itself: user pills, answers next to the orb, the thinking line. */
export function BotFeed(props: { chat: BotChat; intro?: boolean }) {
  const { state, pending, failed, nowMs, send } = props.chat;
  if (!state) return <p className="agima-assistant-muted">{ru.botLoading}</p>;
  if (!state.enabled) return <p className="agima-assistant-muted">{ru.botDisabled}</p>;

  const elapsed = pending ? Math.max(0, Math.round((nowMs - pending.startedAt) / 1000)) : 0;
  const statuses = pending?.statuses ?? [];
  const lastStatus = statuses[statuses.length - 1]?.label || ru.botSending;
  const queued = Boolean(pending) && statuses.length <= 1 && elapsed >= 20;
  const waiting = othersWaiting(state.messages, nowMs);

  return (
    <div className="agima-bot-feed">
      {props.intro !== false && state.messages.length === 0 && !pending && (
        <p className="agima-assistant-muted">{ru.botEmpty}</p>
      )}
      {state.messages
        // Someone else stopping their own question is noise for everyone else.
        .filter((message) => !(message.mine === false && message.role === 'assistant' && message.error === 'aborted'))
        .map((message) =>
        message.role === 'user' ? (
          <div key={message.id} className="agima-bot-user-wrap" data-mine={message.mine === false ? undefined : 'true'}>
            {message.mine === false && message.name && (
              <span className="agima-bot-author">{message.name}</span>
            )}
            <div className="agima-bot-user">{message.text}</div>
          </div>
        ) : (
          <div key={message.id} className="agima-bot-answer">
            <AgimaBotIcon size={26} />
            <div className="agima-bot-answer-body">
              {message.mine === false && message.name && (
                <span className="agima-bot-author">
                  {ru.botAnswerFor} {message.name}
                </span>
              )}
              {message.error ? (
                <p className="agima-bot-error-text">{chatErrorText(message.error)}</p>
              ) : (
                <Markdown text={message.text} />
              )}
            </div>
          </div>
        ),
      )}
      {waiting.map((name) => (
        <div key={`waiting-${name}`} className="agima-bot-answer">
          <AgimaBotIcon size={26} />
          <div className="agima-bot-answer-body">
            <p className="agima-bot-thinking">
              {ru.botAnsweringFor} {name}…
              <span className="agima-bot-caret" aria-hidden="true">
                ▍
              </span>
            </p>
          </div>
        </div>
      ))}
      {pending && (
        <div className="agima-bot-answer" aria-live="polite">
          <AgimaBotIcon size={26} />
          <div className="agima-bot-answer-body">
            <ul className="agima-bot-trace">
              {statuses.slice(0, -1).map((status, n) => (
                <li key={n}>{status.label}</li>
              ))}
            </ul>
            <p className="agima-bot-thinking">
              {queued ? ru.botQueued : lastStatus}… · {elapsed}
              {ru.botSeconds}
              <span className="agima-bot-caret" aria-hidden="true">
                ▍
              </span>
            </p>
            {queued && <p className="agima-assistant-muted">{ru.botQueuedHint}</p>}
          </div>
        </div>
      )}
      {failed && !pending && (
        <div className="agima-bot-error">
          <p className="agima-bot-error-title">⚠ {failed.text}</p>
          <button
            type="button"
            className="agima-bot-retry"
            onClick={() => void send(failed.request, ru.botRetry)}
          >
            {ru.botRetry}
          </button>
        </div>
      )}
      {!state.available && <p className="agima-bot-error-text">{ru.botUnavailable}</p>}
    </div>
  );
}

const MAX_INPUT_HEIGHT = 140;

/** Chips and the input box at the bottom of the panel, as in the Jira widget. */
export function BotComposer(props: { chat: BotChat }) {
  const { state, pending, draft, setDraft, sendDraft, pickChip, abort } = props.chat;
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);

  // Grow with the text up to ~6 lines, then scroll inside.
  React.useLayoutEffect(() => {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, MAX_INPUT_HEIGHT)}px`;
  }, [draft]);

  if (!state?.enabled) return null;
  const busy = Boolean(pending);
  const off = !state.available;

  return (
    <div className="agima-bot-composer">
      {state.chips.length > 0 && (
        <div className="agima-bot-chips">
          {state.chips.map((chip) => (
            <button
              key={chip.id}
              type="button"
              className="agima-bot-chip"
              disabled={busy || off}
              onClick={() => pickChip(chip.id, chip.label)}
            >
              {chip.label}
            </button>
          ))}
        </div>
      )}
      <div className="agima-bot-inputbox">
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              if (!busy) sendDraft();
            }
          }}
          rows={1}
          disabled={off}
          placeholder={busy ? ru.botAnswering : ru.botPlaceholder}
          className="agima-bot-input"
        />
        <div className="agima-bot-inputbar">
          <span className="agima-bot-input-hint">{ru.botInputHint}</span>
          {busy ? (
            <button
              type="button"
              className="agima-bot-send"
              data-busy="true"
              onClick={abort}
              aria-label={ru.botStop}
              title={ru.botStop}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path
                  d="M9 3L5 7l4 4"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              className="agima-bot-send"
              disabled={!draft.trim() || off}
              onClick={sendDraft}
              aria-label={ru.send}
              title={ru.send}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M8 13V4M8 4L4 8M8 4l4 4"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
